import Creatable from "react-select/creatable";
import React, { useState } from "react";
import qapi from "views_shared/app/javascript/api";
import { withAsyncPaginate } from "react-select-async-paginate";

const CreatableAsyncPaginate = withAsyncPaginate(Creatable);

const loadOptions = async (query, _loadedOptions, { page, perPage }) => {
  try {
    const { data, remainingRows } = await qapi.companies.getAll({ query, page, perPage });
    const options = data.map(({ name, id }) => ({ label: name, value: id }));

    return {
      options,
      hasMore: remainingRows > 0,
      additional: { page: page + 1, perPage },
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error loading options:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page, perPage },
    };
  }
};

// Mirrors the media OutletPicker: while a company create is in flight we report it via onCreatingChange
// so the row can disable confirm (a second click would otherwise be swallowed by the single-flight
// employments hook), and a failed create surfaces inline here instead of vanishing into console.error.
// Unlike qapi.mediaOutlets.create (which throws), qapi.companies.create resolves a non-thrown response
// carrying `error`, so both paths route to the same local createError.
function CompanyPicker({ companyName, companyId, onCompanyChange, onCreatingChange = () => {} }) {
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Creating a company also refreshes the company promo-entities mat server-side, so it takes a beat:
  // keep the flag both locally (to drive the control's own spinner via isLoading) and forwarded to the
  // row (which disables confirm). isDisabled is on too — changing the selection while the create is
  // resolving would race the create's onChange and leave the row pointing at the wrong company.
  const setCreatingState = (value) => {
    setCreating(value);
    onCreatingChange(value);
  };

  const handleChange = (option) => {
    setCreateError(null);
    onCompanyChange(option);
  };

  const onCompanyCreate = async (name) => {
    setCreateError(null);
    setCreatingState(true);
    try {
      const response = await qapi.companies.create({ name });
      if (response.error) {
        setCreateError(response.error.message || "Failed to create company");
        return;
      }
      onCompanyChange({ label: name, value: response.data.data.id });
    } catch (error) {
      setCreateError(error.message || "Failed to create company");
    } finally {
      setCreatingState(false);
    }
  };

  return (
    <>
      <CreatableAsyncPaginate
        value={{ label: companyName, value: companyId }}
        loadOptions={loadOptions}
        onChange={handleChange}
        onCreateOption={onCompanyCreate}
        isLoading={creating}
        isDisabled={creating}
        // The editor table sits in a horizontally-scrollable wrapper (overflow != visible), which would
        // otherwise clip this dropdown; portal the menu to the body so it escapes that scroll context.
        // menuPortal zIndex clears the Bootstrap modal; option wraps a long "Create <name>" within the
        // (now bounded) menu width instead of overflowing.
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          option: (base) => ({ ...base, overflowWrap: "anywhere" }),
          menuPortal: (base) => ({ ...base, zIndex: 1060 }),
        }}
        additional={{ page: 1, perPage: 24 }}
        debounceTimeout={300}
        defaultOptions
      />
      {createError && <span className="small text-danger">{createError}</span>}
    </>
  );
}

export default CompanyPicker;
